import { useState, useEffect, useRef } from "react";
import { notification } from "antd";
import { revokeFileSession } from "../libraries/fileAccess";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
// import { useQuery } from "@tanstack/react-query";

import {
  loginSuccess,
  userLogin,
  userLogout,
} from "@lhd/features/users/usersSlice";
import { QUERY_NAME, ROUTES } from "@lhd/constants";
import { useQueryClient } from "@tanstack/react-query";
import {
  getAccessToken,
  getCompanyId,
  removeCookie,
  setCompanyId,
  sleep,
} from "@lhd/utility";
import { resolveLoginCompanyId } from "./users";
import { setCompany } from "@lhd/features/Company/companySlice";
import { Company } from "@lhd/types";
import { useConfirmingLeave } from "../app";
import { find } from "lodash";
import { useFetchMe } from "./users";
import { useFetchCategoryStatusCompanies } from "./categoryStatus";
import { disconnectSharedCable } from "@lhd/libs/actionCable";

export const useReAuthentication = () => {
  const [state, setState] = useState<{
    isLoading: boolean;
    error: any;
  }>({
    isLoading: true,
    error: null,
  });
  const { data: userData, isSuccess, isLoading, isError, error } = useFetchMe();
  const { data: categoryStatusCompaniesData } = useFetchCategoryStatusCompanies(
    { screen: "workers", record_type: "category" },
    isSuccess,
  );
  const dispatch = useDispatch();
  const accessToken = getAccessToken();
  const companyId = getCompanyId();
  // useEffect(() => {
  //   (async () => {
  //     if (!accessToken) {
  //       setState({ error: null, isLoading: false });
  //       return;
  //     } else {
  //       try {
  //         const { data }: { data: User } = await axios({
  //           method: "get",
  //           url: `${SERVICES.AUTH}`,
  //           params: companyId ? { login_company_id: companyId } : {},
  //         });

  //         const companyLogined = companyId
  //           ? find(
  //               data.companies,
  //               (company: Company) => company.id.toString() == companyId,
  //             )
  //           : null;

  //         const companyActive =
  //           find(data.companies, (company: Company) =>
  //             companyLogined
  //               ? company.id.toString() == companyId
  //               : company.is_work_default,
  //           ) || data.companies[0];

  //         if (!getCompanyId() || !companyLogined)
  //           setCompanyId(companyActive.id.toString());

  //         dispatch(userLogin({ user: data }));
  //         dispatch(setCompany({ company: companyActive }));
  //         dispatch(loginSuccess());
  //         setState({ error: null, isLoading: false });
  //       } catch (error) {
  //         localStorage.clear();
  //         // dispatch(userLogout());
  //         setState({
  //           error,
  //           isLoading: false,
  //         });
  //       }
  //     }
  //   })();
  // }, [accessToken, dispatch]);

  useEffect(() => {
    (async () => {
      if (!accessToken) {
        setState({
          error: null,
          isLoading: false,
        });
        return;
      } else {
        if (userData && isSuccess && !isLoading) {
          const companyLogined = companyId
            ? find(
                userData?.data.companies,
                (company: Company) => company.id.toString() == companyId,
              )
            : null;

          const companyActive =
            find(userData?.data.companies, (company: Company) =>
              companyLogined
                ? company.id.toString() == companyId
                : company.is_work_default,
            ) || userData?.data.companies[0];

          if (!getCompanyId() || !companyLogined) {
            setCompanyId(companyActive.id.toString());
          }

          const workingTypeOptions = categoryStatusCompaniesData?.data?.reduce(
            (prevValue, curVal) => ({
              ...prevValue,
              [curVal?.name]: curVal?.name,
            }),
            {},
          );

          userData.data = {
            ...userData?.data,
            workingTypeOptions: workingTypeOptions,
          };

          dispatch(userLogin({ user: userData?.data }));
          dispatch(setCompany({ company: companyActive }));
          dispatch(loginSuccess());
          setState({
            error: null,
            isLoading: false,
          });
        } else if (isError) {
          setState({
            error,
            isLoading: false,
          });
        }
      }
    })();
  }, [
    accessToken,
    categoryStatusCompaniesData?.data,
    companyId,
    dispatch,
    error,
    isError,
    isLoading,
    isSuccess,
    userData,
  ]);

  return { ...state };
};

export const useReAuthenticationOneTime = (callFetchMe = true) => {
  const accessTokenOnInit = getAccessToken();
  const [state, setState] = useState<{
    isLoading: boolean;
    error: any;
    maintenanceNotice?: any;
  }>({
    isLoading: callFetchMe && !!accessTokenOnInit,
    error: null,
    maintenanceNotice: {},
  });

  const dispatch = useDispatch();

  const accessToken = getAccessToken();
  const companyId = getCompanyId();
  const retryCount = useRef(0);
  const {
    data: userData,
    isSuccess,
    isLoading,
    isError,
    error,
    refetch,
  } = useFetchMe(
    resolveLoginCompanyId(companyId),
    callFetchMe && !!accessToken,
  );

  const { data: categoryStatusCompaniesData } = useFetchCategoryStatusCompanies(
    { screen: "workers", record_type: "category" },
    callFetchMe && isSuccess,
  );
  useEffect(() => {
    if (!callFetchMe) {
      setState((prev) => ({
        ...prev,
        error: null,
        isLoading: false,
        maintenanceNotice: {},
      }));
    }
  }, [callFetchMe]);

  useEffect(() => {
    (async () => {
      await sleep(200);

      if (!accessToken) {
        setState({ error: null, isLoading: false, maintenanceNotice: {} });
        return;
      } else {
        if (userData && isSuccess && !isLoading) {
          const maintenanceNotice = userData?.data?.maintenance_notice || [];
          const currentCompanyId = getCompanyId();
          const companyLogined = currentCompanyId
            ? find(
                userData?.data.companies,
                (company: Company) =>
                  Number(company.id) === Number(currentCompanyId),
              )
            : null;

          const companyActive =
            find(userData?.data.companies, (company: Company) =>
              companyLogined
                ? Number(company.id) === Number(currentCompanyId)
                : company.is_work_default,
            ) || userData?.data.companies[0];

          if (!currentCompanyId || !companyLogined) {
            if (retryCount.current < 3) {
              retryCount.current += 1;
              setCompanyId(companyActive.id.toString());
              refetch();
              return;
            }
            if (companyActive) {
              setCompanyId(companyActive.id.toString());
            }
          }

          retryCount.current = 0; // Reset count on success

          const workingTypeOptions = categoryStatusCompaniesData?.data?.reduce(
            (prevValue, curVal) => ({
              ...prevValue,
              [curVal?.name]: curVal?.name,
            }),
            {},
          );

          userData.data = {
            ...userData?.data,
            workingTypeOptions: workingTypeOptions,
          };

          dispatch(userLogin({ user: userData?.data }));
          dispatch(setCompany({ company: companyActive }));
          dispatch(loginSuccess());
          setState({ error: null, isLoading: false, maintenanceNotice });
        } else if (isError) {
          setState({
            error,
            isLoading: false,
            maintenanceNotice: {},
          });
        }
      }
    })();
  }, [
    accessToken,
    callFetchMe,
    categoryStatusCompaniesData?.data,
    companyId,
    dispatch,
    error,
    isError,
    isLoading,
    isSuccess,
    userData,
    refetch,
  ]);

  // Wrapper function to allow passing params to refetch
  const handleAuthen = (companyIdParams?: any) => {
    return refetch(companyIdParams);
  };

  return { ...state, handleAuthen };
};

// export const useReAuthentication = () => {
//   const dispatch = useDispatch();
//   const accessToken = localStorage.getItem("access_token");

//   return useQuery(
//     [QUERY_NAME.CURRENT_USER],
//     () =>
//       axios({
//         method: "get",
//         url: `${SERVICES.AUTH}`,
//       }).then((res) => {
//         dispatch(userLogin({ user: res.data }));
//         dispatch(loginSuccess());
//       }),
//     {
//       retry: 1,
//     },
//   );
// };

export const useLogout = () => {
  const [state, setState] = useState<{
    loading: boolean;
    error: any;
  }>({
    loading: false,
    error: null,
  });

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setConfirmingState } = useConfirmingLeave();

  const handleLogout = async () => {
    try {
      setConfirmingState(null);
      setState({ error: null, loading: true });
      queryClient.cancelQueries();
      queryClient.removeQueries({ queryKey: [QUERY_NAME.ME] });
      await revokeFileSession();
      disconnectSharedCable();
      localStorage.clear();
      sessionStorage.clear();
      removeCookie("access_token");
      dispatch(userLogout());
      navigate(`/${ROUTES.SIGNIN}`, { replace: true });
    } catch (error: any) {
      setState({ error, loading: false });
      notification.error({
        message:
          "ログアウトできませんでした。接続を確認して再試行してください。",
      });
    }
  };

  return { ...state, handleLogout };
};
